/* The studio header piece (prototype: .siteHeader, guest). Colors are theme tokens only. */
@layer telify {
  /* Fixed, not sticky, so nothing below it can move it; body carries its height (web.css). */
  .header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 70;
    background: var(--bg-page);
    border-bottom: var(--line);
  }

  .bar {
    height: var(--head-h);
    display: flex;
    align-items: center;
    gap: 40px;
  }

  .mark {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    margin-right: auto;
  }

  .logo {
    flex-shrink: 0;
    display: block;
    width: 36px;
    height: 36px;
    object-fit: contain;
  }

  .name {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 22px;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* No logo: the name stands alone as the wordmark. */
  .wordmark {
    font-size: 23px;
    letter-spacing: 0;
  }

  .nav {
    display: flex;
    gap: 32px;
  }

  .link {
    font-size: 15px;
    color: var(--text-secondary);
    padding: 6px 0;
    border-bottom: 1px solid transparent;
  }

  .link[aria-current] {
    color: var(--text-primary);
    border-bottom-color: var(--accent);
  }

  .signIn {
    appearance: none;
    cursor: pointer;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    border-radius: var(--r-ctl);
    background: transparent;
    border: 1px solid rgba(var(--accent-rgb), 0.55);
    font-size: 14px;
    font-weight: 500;
    color: var(--accent);
    white-space: nowrap;
  }

  .signIn:disabled {
    cursor: default;
    opacity: 0.6;
  }

  .short {
    display: none;
  }

  /* Signed in: the messages icon with its count, beside the client's menu (prototype .hdrMsg; .hdrEnd's 22px gap). */
  .messages {
    appearance: none;
    border: 0;
    background: none;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    margin-right: -18px;
    display: grid;
    place-items: center;
    border-radius: var(--r-ctl);
    color: var(--text-secondary);
  }

  .messages[aria-expanded='true'] {
    color: var(--text-primary);
    background: var(--tint);
  }

  .messages svg {
    width: 21px;
    height: 21px;
  }

  /* The count: the Home Chat chip's badge (BookingHubPage .quickActionBadge). */
  .count {
    position: absolute;
    top: 2px;
    right: 0;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9999px;
    background: var(--accent);
    color: var(--text-on-accent);
    font-size: 10.5px;
    font-weight: 600;
    line-height: 18px;
    text-align: center;
    box-shadow: 0 0 0 2px var(--bg-page);
  }

  /* Signed in: the client's initials and name, opening the account menu (prototype .who). */
  .who {
    position: relative;
    flex-shrink: 0;
  }

  .user {
    appearance: none;
    background: none;
    border: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    font-size: 14.5px;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  .user[aria-expanded='true'] {
    color: var(--text-primary);
  }

  .user svg {
    width: 14px;
    height: 14px;
    color: var(--text-tertiary);
    transition: transform 0.18s ease;
  }

  .user[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }

  .avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--avatar-bg);
    color: var(--avatar-initials-color);
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
    flex-shrink: 0;
  }

  .menu {
    position: absolute;
    right: 0;
    top: calc(100% + 14px);
    width: 240px;
    padding: 6px 0;
    background: var(--bg-page);
    border: var(--line);
    border-radius: var(--r-ctl);
    box-shadow: var(--shadow-card);
    z-index: 40;
  }

  /* Under Sign out when it did not go through. */
  .menuError {
    margin: 0;
    padding: 8px 16px 10px;
    font-size: 13px;
    line-height: 1.4;
    color: var(--danger);
  }

  .menuItem {
    appearance: none;
    border: 0;
    background: none;
    width: 100%;
    cursor: pointer;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 18px;
    font-size: 15px;
    color: var(--text-primary);
    text-align: left;
  }

  /* Messages' count in the menu (prototype .whoMenu small.new). */
  .menuNew {
    font-size: 13px;
    font-weight: 600;
    color: var(--accent);
  }

  .menuSep {
    height: 1px;
    margin: 6px 0;
    background: var(--border-soft);
  }

  @media (hover: hover) {
    .messages:hover {
      color: var(--text-primary);
      background: var(--tint);
    }

    .link:hover {
      color: var(--text-primary);
      border-bottom-color: var(--text-tertiary);
    }

    .link[aria-current]:hover {
      border-bottom-color: var(--accent);
    }

    .signIn:hover:not(:disabled) {
      background: rgba(var(--accent-rgb), 0.08);
    }

    .user:hover {
      color: var(--text-primary);
    }

    .menuItem:hover {
      background: rgba(var(--fg-page-rgb), 0.05);
    }
  }

  @media (max-width: 1023px) {
    .nav {
      display: none;
    }
  }

  /* At phone width the label shortens to Sign in (the Telegram icon stays), so it fits beside the name. */
  @media (max-width: 560px) {
    .long {
      display: none;
    }

    .short {
      display: inline;
    }

    /* The initials alone beside the studio's name. */
    .userName {
      display: none;
    }
  }
}
